import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_ROLES } from '../../../config/collections'
import { PERMISSION_GROUPS, normalizePermissions, permissionLabel, type PermissionKey } from '../../../config/permissions'
import type { RoleRecord } from '../../../types/records'
import { Alert, ButtonLink, FormField, PageHeader, PageShell, formInputProps } from '../../../components/ui'
import { btn, cn, formActionsClass, formGridClass, permGroupClass, permGroupTitleClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'

export function RoleFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new' || !id

  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const load = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_ROLES).getOne<RoleRecord>(id)
      setName(row.name)
      setTitle(row.title || '')
      setSelected(new Set(normalizePermissions(row.permissions)))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    const slug = name.trim().toLowerCase().replace(/\s+/g, '_')
    if (!/^[a-z0-9_]{2,40}$/.test(slug)) {
      setErr('Код роли: латиница, цифры и _, 2–40 символов.')
      return
    }
    const permissions = Array.from(selected) as PermissionKey[]
    try {
      if (isNew) {
        await pb.collection(COL_ROLES).create({
          name: slug,
          title: title.trim() || slug,
          permissions,
        })
      } else {
        await pb.collection(COL_ROLES).update(id!, {
          name: slug,
          title: title.trim() || slug,
          permissions,
        })
      }
      navigate('/admin/administration/roles', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новая роль' : 'Редактирование роли'}
        lead={
          <>
            Код роли (<code>name</code>) совпадает с полем роли в CRM и с <code>roleName</code> у сотрудника.
          </>
        }
      />
      {err && <Alert kind="err">{err}</Alert>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={cn(formGridClass, 'max-w-[40rem]')}>
          <FormField label="Код (name)" htmlFor="role-name">
            <input
              id="role-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="admin"
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Название" htmlFor="role-title">
            <input
              id="role-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Администратор"
              required
              {...formInputProps()}
            />
          </FormField>
          <FormField label="Права">
            <div className="grid gap-3 sm:grid-cols-2">
              {PERMISSION_GROUPS.map((g) => (
                <div key={g.label} className={permGroupClass}>
                  <div className={permGroupTitleClass}>
                    {g.label}
                  </div>
                  {g.keys.map((key) => (
                    <label key={key} className="block text-sm" title={key}>
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                      />{' '}
                      {permissionLabel(key)}
                    </label>
                  ))}
                </div>
              ))}
              <div className={permGroupClass}>
                <div className={permGroupTitleClass}>
                  Супердоступ
                </div>
                <label className="block text-sm" title="*">
                  <input
                    type="checkbox"
                    checked={selected.has('*')}
                    onChange={() => toggle('*')}
                  />{' '}
                  {permissionLabel('*')}
                </label>
              </div>
            </div>
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <ButtonLink to="/admin/administration/roles" variant="ghost">
              Отмена
            </ButtonLink>
          </div>
        </form>
      )}
    </PageShell>
  )
}
